import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useRef } from 'react';
import { PiXCircle } from 'react-icons/pi';
import Button from 'components/Buttons/Button';
import InputField from 'components/FormFields/InputField';

interface ISetPinModalProps {
  onClose: () => void;
  onSubmit: (data: { name?: string; pin: string }) => void;
  isLoading?: boolean;
  isEditMode?: boolean;
  initialData?: {
    name: string;
    pin: string;
  };
}

const validationSchema = Yup.object({
  name: Yup.string(),
  pin1: Yup.string().required('').matches(/^\d$/, ''),
  pin2: Yup.string().required('').matches(/^\d$/, ''),
  pin3: Yup.string().required('').matches(/^\d$/, ''),
  pin4: Yup.string().required('').matches(/^\d$/, ''),
  pin5: Yup.string().required('').matches(/^\d$/, ''),
  pin6: Yup.string().required('').matches(/^\d$/, ''),
});

const SetPinModal: React.FC<ISetPinModalProps> = ({
  onClose,
  onSubmit,
  isLoading,
  isEditMode = false,
  initialData,
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const getInitialPinValues = () => {
    if (initialData?.pin && initialData.pin.length === 6) {
      return {
        pin1: initialData.pin[0] || '',
        pin2: initialData.pin[1] || '',
        pin3: initialData.pin[2] || '',
        pin4: initialData.pin[3] || '',
        pin5: initialData.pin[4] || '',
        pin6: initialData.pin[5] || '',
      };
    }
    return {
      pin1: '',
      pin2: '',
      pin3: '',
      pin4: '',
      pin5: '',
      pin6: '',
    };
  };

  const formik = useFormik({
    initialValues: {
      name: initialData?.name || '',
      ...getInitialPinValues(),
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const pin = `${values.pin1}${values.pin2}${values.pin3}${values.pin4}${values.pin5}${values.pin6}`;
      const payload: { name?: string; pin: string } = { pin };
      if (values.name.trim()) {
        payload.name = values.name.trim();
      }
      onSubmit(payload);
    },
  });

  const handlePinChange = (index: number, value: string) => {
    const fieldName = `pin${index + 1}` as keyof typeof formik.values;

    if (value.length > 1) {
      value = value.slice(-1);
    }

    if (value && !/^\d$/.test(value)) {
      return;
    }

    formik.setFieldValue(fieldName, value);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      const fieldName = `pin${index + 1}` as keyof typeof formik.values;
      if (!formik.values[fieldName] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const isPinComplete =
    formik.values.pin1 &&
    formik.values.pin2 &&
    formik.values.pin3 &&
    formik.values.pin4 &&
    formik.values.pin5 &&
    formik.values.pin6;

  return (
    <div className="w-full flex flex-col gap-6 p-6">
      <div className="flex items-center justify-between">
        <h2 className="font-poppins font-semibold text-[18px] leading-[100%] tracking-[0%] text-darkBlue">
          {isEditMode ? 'Edit Pin' : 'Set Pin'}
        </h2>
        <button
          onClick={onClose}
          className="flex items-center justify-center hover:opacity-80 transition-opacity"
          type="button"
        >
          <PiXCircle className="w-6 h-6 text-darkBlue" />
        </button>
      </div>

      <p className="font-poppins font-normal text-[14px] leading-[150%] tracking-[0%] text-darkBlue">
        This pin will be used by the cashier to log into the merchant app and scan for
        subscriptions.
      </p>

      <form onSubmit={formik.handleSubmit} className="flex flex-col gap-6">
        <div>
          <label className="block mb-2 font-poppins font-normal text-[12px] leading-[100%] tracking-[0%] text-darkBlack">
            Pin name
          </label>
          <InputField
            value={formik.values.name}
            onChange={formik.handleChange('name')}
            placeholder="Enter pin name"
            error={formik.touched.name && formik.errors.name}
            customClass="!w-full !h-[40px] !py-[10px] !px-[16px] !border !rounded-[4px]"
            containerClass="!mb-0"
          />
        </div>

        <div>
          <label className="block mb-2 font-poppins font-normal text-[12px] leading-[100%] tracking-[0%] text-darkBlack">
            Pin<span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2 w-full md:justify-between">
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={formik.values[`pin${index + 1}` as keyof typeof formik.values]}
                onChange={(e) => handlePinChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-[44px] h-[44px] md:w-[52px] md:h-[52px] text-center font-poppins font-medium text-[18px] md:text-[22px] leading-[100%] tracking-[0%] text-darkBlue border border-[#D8D9F0] rounded-[8px] focus:ring-2 focus:ring-primary focus:border-primary outline-none"
              />
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button
            label="Cancel"
            onClick={onClose}
            variant="secondary"
            customClass="!w-[91px] !h-[33px] !py-[6px] !px-[20px] !mb-0 !rounded-[40px] !border !border-primary !text-primary !gap-2"
          />
          <Button
            label={isEditMode ? 'Update' : '+ Add pin'}
            type="submit"
            disabled={!isPinComplete || isLoading}
            loading={isLoading}
            customClass="!w-[109px] !h-[33px] !py-[6px] !px-[20px] !mb-0 !rounded-[40px] !gap-2"
          />
        </div>
      </form>
    </div>
  );
};

export default SetPinModal;
